<template>
  <div>
    <View wrap="wrap" justify="space-between" direction="row" :grow="1" gap="lg">
      <View gap="md" align="center" class="title-left">
        <PropertyPhotoPreview :image-url="coverImage" size="md" />

        <View direction="column" gap="md">
          <View gap="sm" class="title" wrap="wrap-reverse" align="center">
            <Text variant="h1">{{ propertyStore.property?.displayName }}</Text>
            <View gap="sm" align="center">
              <PropertyStatusLabel :status="propertyStatus" />
              <View gap="sm" v-if="propertyStatus === PropertyStatus.LISTED && listedAt">
                <Text variant="body-medium" color="secondary">∙</Text>
                <Text variant="body-medium" color="secondary">{{ listedAt }}</Text>
              </View>
            </View>
          </View>

          <View gap="sm" wrap="wrap" class="actions">
            <Button
              v-if="isVisibleStopListingButton"
              variant="outline"
              size="medium"
              data-manage-action
              @click="pauseListing"
            >
              <Icon name="pause-circle" size="small" />
              <span>Pause listing</span>
            </Button>

            <Button
              v-else-if="isVisibleStartListingButton"
              variant="outline"
              size="medium"
              data-manage-action
              @click="startListing"
            >
              <Icon name="start" size="small" />
              <span>{{ startListingLabel }}</span>
            </Button>

            <Button
              v-if="isVisibleMarkAsRentedOutButton"
              variant="outline"
              appearance="success"
              size="medium"
              data-manage-action
              @click="markAsRentedOut"
            >
              <Icon name="check-circle" size="small" />
              <span>Mark as Rented</span>
            </Button>
          </View>
        </View>
      </View>

      <View direction="column" gap="sm" align="end" class="title-right">
        <View gap="sm" wrap="wrap" justify="end">
          <Button class="add-new-lead" variant="outline" size="medium" data-manage-action @click="addNewLead">
            <Icon name="user-plus-01" size="small" />
            <span>Add new lead</span>
          </Button>

          <ApplyLinkButton
            v-if="isVisibleApplyLinkButton"
            :property-id="propertyId"
            :address="propertyStore.property?.displayName ?? ''"
            :is-rented-out="propertyStatus === PropertyStatus.RENTED_OUT"
            ref="applyLinkButtonRef"
          />
        </View>

        <View v-if="isVisibleListedOn" gap="sm" align="center" class="listed-on">
          <Text variant="body-medium" color="secondary">Listed on</Text>
          <SyndicationPlatformsList :platforms="platforms" />
          <IconButton
            icon="pencil"
            variant="ghost"
            shape="square"
            size="small"
            aria-label="Edit syndication platforms"
            data-manage-action
            @click="openSyndication"
          />
        </View>
      </View>
    </View>

    <AddNewLeadModal :propertyId="propertyId" ref="addNewLeadModalModalRef" />
    <PropertyMarkAsRentedOutModal ref="markAsRentedOutModalRef" />
    <PropertyPauseListingModal ref="pauseModalRef" />
    <PropertyListModal ref="resumeModalRef" />
    <PropertySyndicationPlatformsModal ref="syndicationModalRef" />
  </div>
</template>

<script setup lang="ts">
import { useTimeAgo } from '@vueuse/core';
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { Button, Icon, IconButton, Text, View } from '@tallo/design-system';
import {
  PropertyStatusLabel,
  SyndicationPlatformsList,
  usePropertyStore,
  usePropertySyndicationPlatformsQuery,
} from '@tallo/investor/property';
import { PropertyPhotoPreview, PropertyStatus } from '@tallo/property';

import PropertyListModal from '../../components/property-list-modal.vue';
import PropertyMarkAsRentedOutModal from '../../components/property-mark-as-rented-out-modal.vue';
import PropertyPauseListingModal from '../../components/property-pause-listing-modal.vue';
import PropertySyndicationPlatformsModal from '../../components/property-syndication-platforms-modal.vue';
import AddNewLeadModal from './add-new-lead-modal.vue';
import ApplyLinkButton from './apply-link-modal/apply-link-button.vue';
import { applyLinkUnitStatuses } from './apply-link-modal/apply-link-unit-statuses';

const route = useRoute();
const router = useRouter();
const propertyStore = usePropertyStore();
const { platforms } = usePropertySyndicationPlatformsQuery();
const propertyId = computed(() => route.params.propertyId as string);

const addNewLeadModalModalRef = ref<InstanceType<typeof AddNewLeadModal>>();
const applyLinkButtonRef = ref<InstanceType<typeof ApplyLinkButton>>();
const markAsRentedOutModalRef = ref<InstanceType<typeof PropertyMarkAsRentedOutModal>>();
const pauseModalRef = ref<InstanceType<typeof PropertyPauseListingModal>>();
const resumeModalRef = ref<InstanceType<typeof PropertyListModal>>();
const syndicationModalRef = ref<InstanceType<typeof PropertySyndicationPlatformsModal>>();

const propertyStatus = computed(() => propertyStore.property?.status!);
const coverImage = computed(() => propertyStore.property?.coverImage);
const listedAt = computed(() =>
  propertyStore.property?.lastListedAt ? useTimeAgo(propertyStore.property?.lastListedAt) : null,
);
const startListingLabel = computed(() =>
  propertyStatus.value === PropertyStatus.READY_FOR_LISTING ? 'List property' : 'Resume listing',
);

const isVisibleStartListingButton = computed(() =>
  [
    PropertyStatus.READY_FOR_LISTING,
    PropertyStatus.UNLISTED,
    PropertyStatus.RENTED_OUT,
    PropertyStatus.DRAFT,
    PropertyStatus.SYNDICATION_ERROR,
  ].includes(propertyStatus.value!),
);

const isVisibleStopListingButton = computed(() =>
  [PropertyStatus.LISTING_IN_PROGRESS, PropertyStatus.LISTED].includes(propertyStatus.value!),
);

const isVisibleMarkAsRentedOutButton = computed(() =>
  [PropertyStatus.LISTING_IN_PROGRESS, PropertyStatus.LISTED, PropertyStatus.UNLISTED].includes(propertyStatus.value!),
);

// Listed or not: an owner may take applications through Tallo without syndicating through it
const isVisibleApplyLinkButton = computed(() => applyLinkUnitStatuses.includes(propertyStatus.value));

// "Listed on" applies once the listing has gone out — while it's still syndicating (in progress)
// and once it's live (listed).
const isVisibleListedOn = computed(() =>
  [PropertyStatus.LISTED, PropertyStatus.LISTING_IN_PROGRESS].includes(propertyStatus.value!),
);

function addNewLead() {
  addNewLeadModalModalRef.value?.modalRef?.show();
}

// `?applyLink=1` is the deep link Emily and the owner notices hand out: open the apply link on arrival, and drop
// the flag first so a refresh or back-navigation does not reopen it. The flag is watched, not only read on mount:
// an owner already on this dashboard who follows the link from the chat drawer changes nothing but the query. A
// link to another unit is left alone: the store still holds this unit while that one loads, and that unit's own
// title consumes the flag once the page has mounted it.
async function openApplyLinkFromQuery(): Promise<void> {
  if (route.query.applyLink !== '1' || propertyStore.property?.id !== propertyId.value) return;

  const query = { ...route.query };
  delete query.applyLink;
  await router.replace({ query });

  if (isVisibleApplyLinkButton.value) {
    await applyLinkButtonRef.value?.open();
  }
}

onMounted(openApplyLinkFromQuery);
watch(() => route.query.applyLink, openApplyLinkFromQuery);

function startListing() {
  resumeModalRef.value?.modalRef?.show();
}

function pauseListing() {
  pauseModalRef.value?.modalRef?.show();
}

function markAsRentedOut() {
  markAsRentedOutModalRef.value?.modalRef?.show();
}

function openSyndication() {
  syndicationModalRef.value?.modalRef?.show();
}
</script>

<style scoped lang="scss">
@import 'styles/mixins/layout.mixins';

// The platforms row beside it is hidden on a phone, so the column is a lone button there. Given the
// line's width it lands where it belongs - the end of the row, as on a desktop - instead of being
// stranded mid-line by a column shrink-wrapped to the button.
.title-right {
  // With both buttons the column can wrap under the title; it still belongs at the end of the row
  margin-left: auto;

  @include mobile {
    width: 100%;
  }
}

.listed-on {
  @include mobile {
    display: none;
  }
}
</style>
